/**
 * Re-queues a failed OncoEMR task reply outbox row for another delivery attempt.
 * @module retryOncoemrTaskReply
 */

import type {
  RetryOncoemrTaskReplyRequest,
  RetryOncoemrTaskReplyResponse,
} from "../../data-model/oncoemrTasksModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { formatErrorDetail } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * Builds the URL for `POST /oncoemr-tasks/replies/{idempotency_key}/retry`.
 *
 * @param idempotencyKey - Client-generated key on the original reply request.
 * @returns Absolute request URL.
 */
export const buildOncoemrTaskReplyRetryUrl = (idempotencyKey: string): string =>
  `${API_ENDPOINTS.ONCOEMR_TASKS}/replies/${encodeURIComponent(idempotencyKey)}/retry`;

/**
 * Re-queues a failed outbox reply; the same row is reused (no new insert).
 *
 * @param idempotencyKey - Outbox row idempotency key from the thread payload.
 * @param body - Organization scope (`org_id`).
 * @param signal - Optional abort signal.
 * @returns Updated outbox status and user-facing confirmation text.
 * @throws Error When the reply is not found, not failed, or the request otherwise fails.
 */
export const retryOncoemrTaskReply = async (
  idempotencyKey: string,
  body: RetryOncoemrTaskReplyRequest,
  signal?: AbortSignal,
): Promise<RetryOncoemrTaskReplyResponse> => {
  const bearerToken = await getBearerToken();

  const response = await fetch(buildOncoemrTaskReplyRetryUrl(idempotencyKey), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    ...(signal && { signal }),
  });

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    throw new Error(formatErrorDetail(errorData, response.statusText));
  }

  return (await response.json()) as RetryOncoemrTaskReplyResponse;
};
